/**
 * Joint axis and rotation direction visualization component
 */

import { useMemo } from "react";
import * as THREE from "three";
import { Line } from "@react-three/drei";
import { useJointAxisVisualization } from "../store";

// Create an arrow shape for the axis indicator
function AxisArrow({
  direction,
  color,
  length = 0.15,
}: {
  direction: THREE.Vector3;
  color: string;
  length?: number;
}) {
  // Create arrow geometry using cylinder for shaft and cone for head
  const { shaftGeom, headGeom, quaternion } = useMemo(() => {
    const shaftRadius = 0.003;
    const headRadius = 0.008;
    const headLength = 0.02;
    const shaftLength = length - headLength;

    // Shaft (cylinder)
    const shaft = new THREE.CylinderGeometry(shaftRadius, shaftRadius, shaftLength, 8);
    shaft.translate(0, shaftLength / 2, 0);

    // Head (cone)
    const head = new THREE.ConeGeometry(headRadius, headLength, 8);
    head.translate(0, shaftLength + headLength / 2, 0);

    // Calculate rotation to align with direction
    const up = new THREE.Vector3(0, 1, 0);
    const quat = new THREE.Quaternion();
    quat.setFromUnitVectors(up, direction.clone().normalize());

    return { shaftGeom: shaft, headGeom: head, quaternion: quat };
  }, [direction, length]);

  return (
    <group quaternion={quaternion}>
      <mesh geometry={shaftGeom}>
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh geometry={headGeom}>
        <meshStandardMaterial color={color} />
      </mesh>
    </group>
  );
}

// Create a circular arrow showing rotation direction (right-hand rule)
function RotationDirectionArrow({
  axis,
  radius = 0.04,
  color = "#00d9ff",
}: {
  axis: THREE.Vector3;
  radius?: number;
  color?: string;
}) {
  // Create circular arc (about 270 degrees) with arrow head
  const { arcPoints, arrowHeadGeom, arrowQuaternion } = useMemo(() => {
    const segments = 24;
    const arcAngle = Math.PI * 1.5; // 270 degrees
    const startAngle = -Math.PI * 0.25; // Start at -45 degrees
    const points: [number, number, number][] = [];

    // Create arc points in XY plane
    for (let i = 0; i <= segments; i++) {
      const angle = startAngle + (arcAngle * i) / segments;
      points.push([Math.cos(angle) * radius, Math.sin(angle) * radius, 0]);
    }

    // Arrow head at the end of the arc
    const endAngle = startAngle + arcAngle;
    const headRadius = 0.012;
    const headLength = 0.018;
    const head = new THREE.ConeGeometry(headRadius, headLength, 8);

    // Position arrow head at end of arc, pointing tangent to arc (positive rotation)
    // Tangent direction at endAngle is (-sin(endAngle), cos(endAngle), 0)
    const headQuat = new THREE.Quaternion();
    // First align cone (which points up by default) to point in tangent direction
    const up = new THREE.Vector3(0, 1, 0);
    const tangent = new THREE.Vector3(-Math.sin(endAngle), Math.cos(endAngle), 0);
    headQuat.setFromUnitVectors(up, tangent);

    return { arcPoints: points, arrowHeadGeom: head, arrowQuaternion: headQuat };
  }, [radius]);

  // Arrow head position
  const arrowHeadPosition = useMemo((): [number, number, number] => {
    const endAngle = -Math.PI * 0.25 + Math.PI * 1.5;
    return [Math.cos(endAngle) * radius, Math.sin(endAngle) * radius, 0];
  }, [radius]);

  // Calculate rotation to align arc plane perpendicular to axis
  const quaternion = useMemo(() => {
    const up = new THREE.Vector3(0, 0, 1);
    const quat = new THREE.Quaternion();
    quat.setFromUnitVectors(up, axis.clone().normalize());
    return quat;
  }, [axis]);

  return (
    <group quaternion={quaternion}>
      {/* Circular arc */}
      <Line
        points={arcPoints}
        color={color}
        lineWidth={4}
      />
      {/* Arrow head */}
      <mesh position={arrowHeadPosition} quaternion={arrowQuaternion} geometry={arrowHeadGeom}>
        <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.3} />
      </mesh>
    </group>
  );
}

// Create a rotation arc indicator for revolute joints
function RotationArc({
  axis,
  minAngle,
  maxAngle,
  currentAngle,
  radius = 0.08,
}: {
  axis: THREE.Vector3;
  minAngle: number;
  maxAngle: number;
  currentAngle: number;
  radius?: number;
}) {
  // Create arc points showing the rotation range
  const arcPoints = useMemo(() => {
    const segments = 32;
    const arcAngle = maxAngle - minAngle;
    const points: [number, number, number][] = [];

    // Create arc points
    for (let i = 0; i <= segments; i++) {
      const angle = minAngle + (arcAngle * i) / segments;
      points.push([Math.cos(angle) * radius, Math.sin(angle) * radius, 0]);
    }

    return points;
  }, [minAngle, maxAngle, radius]);

  // Create current angle indicator line points
  const currentAnglePoints = useMemo((): [number, number, number][] => {
    return [
      [0, 0, 0],
      [Math.cos(currentAngle) * radius * 1.3, Math.sin(currentAngle) * radius * 1.3, 0],
    ];
  }, [currentAngle, radius]);

  // Create arrow head points at current angle position (showing rotation direction)
  const arrowHeadPoints = useMemo((): [number, number, number][] => {
    const arrowSize = 0.012;
    const endX = Math.cos(currentAngle) * radius * 1.3;
    const endY = Math.sin(currentAngle) * radius * 1.3;

    // Direction tangent to the arc (positive rotation direction)
    const tangentX = -Math.sin(currentAngle);
    const tangentY = Math.cos(currentAngle);

    return [
      [endX, endY, 0],
      [
        endX - tangentX * arrowSize - Math.cos(currentAngle) * arrowSize * 0.5,
        endY - tangentY * arrowSize - Math.sin(currentAngle) * arrowSize * 0.5,
        0,
      ],
      [endX, endY, 0],
      [
        endX - tangentX * arrowSize + Math.cos(currentAngle) * arrowSize * 0.5,
        endY - tangentY * arrowSize + Math.sin(currentAngle) * arrowSize * 0.5,
        0,
      ],
    ];
  }, [currentAngle, radius]);

  // Calculate rotation to align arc plane perpendicular to axis
  const quaternion = useMemo(() => {
    const up = new THREE.Vector3(0, 0, 1);
    const quat = new THREE.Quaternion();
    quat.setFromUnitVectors(up, axis.clone().normalize());
    return quat;
  }, [axis]);

  return (
    <group quaternion={quaternion}>
      {/* Range arc (semi-transparent blue) */}
      <Line
        points={arcPoints}
        color="#4dabf7"
        lineWidth={2}
        opacity={0.6}
        transparent
      />

      {/* Current angle indicator (yellow) */}
      <Line
        points={currentAnglePoints}
        color="#ffd43b"
        lineWidth={3}
      />

      {/* Arrow head showing rotation direction */}
      <Line
        points={arrowHeadPoints}
        color="#ffd43b"
        lineWidth={2}
      />
    </group>
  );
}

// Create prismatic (linear) motion indicator
function PrismaticIndicator({
  axis,
  length = 0.1,
}: {
  axis: THREE.Vector3;
  length?: number;
}) {
  const linePoints = useMemo((): [number, number, number][] => {
    const dir = axis.clone().normalize();
    return [
      [dir.x * -length, dir.y * -length, dir.z * -length],
      [dir.x * length, dir.y * length, dir.z * length],
    ];
  }, [axis, length]);

  // Create double-headed arrow points
  const arrowHeadPoints = useMemo((): [number, number, number][] => {
    const dir = axis.clone().normalize();
    const arrowSize = 0.015;

    // Find perpendicular vector for arrow heads
    const perp = new THREE.Vector3();
    if (Math.abs(dir.x) < 0.9) {
      perp.crossVectors(dir, new THREE.Vector3(1, 0, 0));
    } else {
      perp.crossVectors(dir, new THREE.Vector3(0, 1, 0));
    }
    perp.normalize();

    const end1 = dir.clone().multiplyScalar(length);
    const end2 = dir.clone().multiplyScalar(-length);
    const back1 = end1.clone().sub(dir.clone().multiplyScalar(arrowSize));
    const back2 = end2.clone().add(dir.clone().multiplyScalar(arrowSize));

    return [
      // Arrow at positive end
      [end1.x, end1.y, end1.z],
      [back1.x + perp.x * arrowSize * 0.5, back1.y + perp.y * arrowSize * 0.5, back1.z + perp.z * arrowSize * 0.5],
      [end1.x, end1.y, end1.z],
      [back1.x - perp.x * arrowSize * 0.5, back1.y - perp.y * arrowSize * 0.5, back1.z - perp.z * arrowSize * 0.5],
      // Arrow at negative end
      [end2.x, end2.y, end2.z],
      [back2.x + perp.x * arrowSize * 0.5, back2.y + perp.y * arrowSize * 0.5, back2.z + perp.z * arrowSize * 0.5],
      [end2.x, end2.y, end2.z],
      [back2.x - perp.x * arrowSize * 0.5, back2.y - perp.y * arrowSize * 0.5, back2.z - perp.z * arrowSize * 0.5],
    ];
  }, [axis, length]);

  return (
    <group>
      {/* Main axis line (green) */}
      <Line
        points={linePoints}
        color="#69db7c"
        lineWidth={3}
      />
      {/* Arrow heads */}
      <Line
        points={arrowHeadPoints}
        color="#69db7c"
        lineWidth={2}
      />
    </group>
  );
}

export function JointAxisVisualization() {
  const axisVis = useJointAxisVisualization();

  if (!axisVis || !axisVis.show) {
    return null;
  }

  const position = new THREE.Vector3(...axisVis.position);
  const axis = new THREE.Vector3(...axisVis.axis);
  const isRevolute = axisVis.jointType === "revolute" || axisVis.jointType === "continuous";
  const isPrismatic = axisVis.jointType === "prismatic";

  return (
    <group position={position}>
      {/* Joint axis arrow (red - always show) */}
      <AxisArrow direction={axis} color="#ff6b6b" length={0.1} />
      <AxisArrow direction={axis.clone().negate()} color="#ff8787" length={0.05} />

      {/* Rotation indicator for revolute/continuous joints */}
      {isRevolute && (
        <>
          {/* Circular arrow showing rotation direction */}
          <RotationDirectionArrow axis={axis} radius={0.07} color="#ff9500" />

          {/* Range arc and current angle indicator */}
          <RotationArc
            axis={axis}
            minAngle={axisVis.minAngle}
            maxAngle={axisVis.maxAngle}
            currentAngle={axisVis.currentAngle}
            radius={0.09}
          />
        </>
      )}

      {/* Linear motion indicator for prismatic joints */}
      {isPrismatic && <PrismaticIndicator axis={axis} length={0.08} />}

      {/* Small sphere at joint origin */}
      <mesh>
        <sphereGeometry args={[0.006, 16, 16]} />
        <meshStandardMaterial color="#ff6b6b" emissive="#ff6b6b" emissiveIntensity={0.3} />
      </mesh>
    </group>
  );
}
